import { Component, Injector, OnInit } from '@angular/core';
import { NotificationType } from '@modeso/aumico-lib-core-fe';
import {
    AiOrgSettingsService,
    LocalizedString,
    OrgAiSettings,
    masterCovers,
    pickLang,
} from '@modeso/aumico-lib-ai-agents-fe';
import { OrganizationProvider } from '@modeso/aumico-lib-users-fe';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { I18NextService } from 'angular-i18next';
import { filter, switchMap, take } from 'rxjs';
import { BasePageComponent } from '../../../../pages/base.page';

/** A card under «Verfügbare KI-Funktionen». */
interface AiFeatureCard {
    readonly id: string;
    readonly icon: string;
    readonly titleKey: string;
    readonly textKey: string;
}

/**
 * The features a customer knows by name. Defined here, not from the server's agent list: that list
 * also holds internal steps (document reading, classification) that mean nothing on their own.
 */
const AI_FEATURE_CARDS: readonly AiFeatureCard[] = [
    {
        id: 'closing',
        icon: 'clipboard-list',
        titleKey: 'aumico_ai_settings_feature_closing_title',
        textKey: 'aumico_ai_settings_feature_closing_text',
    },
    {
        id: 'translate-report',
        icon: 'file-text',
        titleKey: 'aumico_ai_settings_feature_translate_title',
        textKey: 'aumico_ai_settings_feature_translate_text',
    },
    {
        id: 'datev-repair',
        icon: 'database',
        titleKey: 'aumico_ai_settings_feature_datev_title',
        textKey: 'aumico_ai_settings_feature_datev_text',
    },
];

/**
 * AiSettingsPage — KI-Einstellungen tab.
 *
 * One switch: "KI-Funktionen aktivieren". Enabling opens the master AGB first; once accepted, every
 * AI feature of the organisation may run, within the token limits shown below.
 *
 * `ham-toggle`'s `<input [checked]>` does not snap back after the browser's own click flipped it
 * when the bound value ends up unchanged (a declined modal, a failed write), so the toggle is
 * briefly removed and re-added to re-mount from the model value.
 */
@UntilDestroy()
@Component({
    selector: 'app-page-ai-settings',
    templateUrl: './ai-settings.page.html',
    styleUrls: ['./ai-settings.page.scss'],
})
export class AiSettingsPage extends BasePageComponent implements OnInit {
    settings: OrgAiSettings | null = null;
    isLoading = true;
    masterToggleLoading = false;

    /** Acceptance modal state — shown when user toggles ON. */
    showAcceptanceModal = false;

    /** False while the master toggle is briefly removed to re-mount (see class doc). */
    masterToggleVisible = true;

    readonly featureCards = AI_FEATURE_CARDS;

    private organizationId = '';

    public constructor(
        injector: Injector,
        private readonly aiOrgSettingsService: AiOrgSettingsService,
        private readonly organizationProvider: OrganizationProvider,
        private readonly i18NextService: I18NextService,
    ) {
        super(injector);
    }

    public override ngOnInit(): void {
        super.ngOnInit();
        this.loadSettings();
    }

    private loadSettings(): void {
        this.isLoading = true;
        this.organizationProvider
            .getCurrentOrganization$()
            .pipe(
                filter((org) => !!org),
                take(1),
                switchMap((org) => {
                    this.organizationId = org.id;
                    return this.aiOrgSettingsService.getOrgSettings$(org.id);
                }),
                untilDestroyed(this),
            )
            .subscribe({
                next: (s) => {
                    this.settings = s;
                    this.isLoading = false;
                },
                error: () => {
                    this.isLoading = false;
                },
            });
    }

    /**
     * True iff the master toggle is on AND the accepted version matches the
     * current master AGB version. A version mismatch (admin updated AGB
     * after acceptance) renders the toggle as off so the user re-accepts.
     */
    public get isMasterEffectivelyEnabled(): boolean {
        return this.settings ? masterCovers(this.settings) : false;
    }

    /**
     * Toggle handler. Enabling opens the acceptance modal first (the actual
     * API call is deferred until the user clicks "Accept"). Disabling is
     * immediate.
     */
    public onMasterToggleChange(enabled: boolean): void {
        if (enabled) {
            this.showAcceptanceModal = true;
            return;
        }
        this.persistMaster(false);
    }

    public confirmAcceptance(): void {
        if (!this.settings) return;
        this.showAcceptanceModal = false;
        this.persistMaster(true, this.settings.masterAgb.version);
    }

    public cancelAcceptance(): void {
        this.showAcceptanceModal = false;
        this.remountMasterToggle();
    }

    private persistMaster(enabled: boolean, acceptedVersion?: number): void {
        this.masterToggleLoading = true;

        const body = {
            enabled,
            ...(acceptedVersion !== undefined ? { acceptedVersion } : {}),
        };

        this.aiOrgSettingsService
            .setAiFeatures$(this.organizationId, body)
            .pipe(untilDestroyed(this))
            .subscribe({
                next: () => {
                    if (this.settings) {
                        this.settings = {
                            ...this.settings,
                            aiFeaturesEnabled: enabled,
                            aiFeaturesAcceptedVersion: enabled
                                ? (acceptedVersion ?? null)
                                : null,
                        };
                    }
                    this.masterToggleLoading = false;
                    this.aumicoCoreProvider.dispatchAddNotification(
                        'aumico_ai_settings_notification_success_title',
                        enabled
                            ? 'aumico_ai_settings_notification_enabled'
                            : 'aumico_ai_settings_notification_disabled',
                        NotificationType.SUCCESS,
                        3000,
                    );
                },
                error: (err: { status?: number }) => {
                    this.masterToggleLoading = false;
                    this.remountMasterToggle();
                    if (err.status === 409) {
                        this.aumicoCoreProvider.dispatchAddNotification(
                            'aumico_ai_settings_notification_error_title',
                            'aumico_ai_settings_notification_version_changed',
                            NotificationType.ERROR,
                            5000,
                        );
                        this.loadSettings();
                        return;
                    }
                    this.aumicoCoreProvider.dispatchAddNotification(
                        'aumico_ai_settings_notification_error_title',
                        'aumico_ai_settings_notification_error',
                        NotificationType.ERROR,
                        3000,
                    );
                },
            });
    }

    private remountMasterToggle(): void {
        this.masterToggleVisible = false;
        setTimeout(() => {
            this.masterToggleVisible = true;
        }, 0);
    }

    /** Display helper — picks the right localized string for the active app language. */
    public pickText(value: LocalizedString | null | undefined): string {
        return pickLang(value, this.i18NextService.language || 'de-ch');
    }

    public formatNumber(n: number): string {
        return new Intl.NumberFormat('de-CH').format(n);
    }

    /** Percentage 0..100 of daily limit consumed (0 if no limit set). */
    public get dailyUsagePct(): number {
        return this.computePct(this.settings?.currentDailyTokens, this.settings?.dailyTokenLimit);
    }

    /** Percentage 0..100 of monthly limit consumed (0 if no limit set). */
    public get monthlyUsagePct(): number {
        return this.computePct(this.settings?.currentMonthlyTokens, this.settings?.monthlyTokenLimit);
    }

    // eslint-disable-next-line @typescript-eslint/class-methods-use-this
    private computePct(current: number | undefined, limit: number | null | undefined): number {
        if (current == null || limit == null || limit <= 0) return 0;
        return Math.min(100, Math.round((current / limit) * 100));
    }
}
